/**
 * A template bilaga of a beta contract as its own document (ADR-0102
 * addendum 3): its form comes from its own composition
 * (`describeCompositionForm` / `computeFormState`, the same renderer as
 * the main form), its values are its own (`contract_attachments.values`),
 * and fields mapped from the main document follow it live ("Hämtas
 * automatiskt från huvudavtalet") until switched to Manuell — per field,
 * revertible. Text adaptation of the bilaga lives under Anpassa text
 * (document switcher). Local buffer + explicit save, like the legacy
 * bilaga editor.
 */

import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  AUTOFILL_MANUAL_KEY,
  computeFormState,
  describeCompositionForm,
  toOptionCodes,
  type CompositionForm,
} from '@hyranai/regta-document-ir';
import { createRegtaBlockRegistry, getComposition } from '@hyranai/regta-blocks';
import { getTemplateVersion } from '@hyranai/regta-templates';
import { ApiError, tenantApi, type ContractAttachment, type IrPreview } from '../../api.js';
import { irErrorText } from '../../lib/ir-errors.js';
import { BetaFormGroups } from './BetaFormGroups.js';
import { IrPreviewFrame } from './IrPreview.js';
import { customValuesErrorText, effectiveValues, withCustomFields, withLegacyAutofill, type EditorField } from './form-model.js';

let blockRegistry: ReturnType<typeof createRegtaBlockRegistry> | null = null;
const registry = () => (blockRegistry ??= createRegtaBlockRegistry());

const LEGACY_OVERRIDE_PREFIX = '__override_';

/** The manual set as the form shows it: the framework list plus legacy `__override_<key>` markers. */
function withLegacyOverrides(values: Record<string, unknown>): Record<string, unknown> {
  const manual = new Set(Array.isArray(values[AUTOFILL_MANUAL_KEY]) ? (values[AUTOFILL_MANUAL_KEY] as string[]) : []);
  for (const [k, v] of Object.entries(values)) {
    if (k.startsWith(LEGACY_OVERRIDE_PREFIX) && v === true) manual.add(k.slice(LEGACY_OVERRIDE_PREFIX.length));
  }
  return manual.size > 0 ? { ...values, [AUTOFILL_MANUAL_KEY]: [...manual] } : values;
}

/** True when this bilaga is its own beta document (beta parent + IR twin). */
export function isBetaBilaga(attachment: ContractAttachment): boolean {
  return (
    attachment.kind === 'template' &&
    !!attachment.templateKey &&
    !!attachment.templateVersion &&
    getComposition(attachment.templateKey, attachment.templateVersion) !== null
  );
}

export function BetaBilagaEditor({
  contractId,
  attachment,
  disabled,
  onChanged,
}: {
  contractId: string;
  attachment: ContractAttachment;
  disabled: boolean;
  onChanged: () => Promise<void> | void;
}): ReactElement {
  const templateKey = attachment.templateKey!;
  const templateVersion = attachment.templateVersion!;
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...(attachment.values ?? {}) }));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<IrPreview | null>(null);
  const [log, setLog] = useState<Parameters<typeof withCustomFields>[1]>(null);

  useEffect(() => {
    setValues({ ...(attachment.values ?? {}) });
    setDirty(false);
    setErr(null);
  }, [attachment.id, attachment.values]);

  // Own fields of this bilaga are defined in its own action log.
  useEffect(() => {
    let alive = true;
    tenantApi.contracts
      .finetune_get({ contractId, attachmentId: attachment.id })
      .then((v) => alive && setLog(v.log))
      .catch(() => alive && setLog(null));
    return () => {
      alive = false;
    };
  }, [contractId, attachment.id]);

  // Live preview of this bilaga (mapped fields resolved from the main document server side).
  const valuesJson = JSON.stringify(values);
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(() => {
      tenantApi.contracts
        .ir_preview({ contractId, attachmentValues: { [attachment.id]: JSON.parse(valuesJson) as Record<string, unknown> } })
        .then((p) => alive && setPreview(p))
        .catch((e: unknown) => alive && setErr(irErrorText((e as Error).message, 'Kunde inte förhandsvisa bilagan')));
    }, 400);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [contractId, attachment.id, valuesJson]);

  const compiled = preview?.attachments?.find((a) => a.attachmentId === attachment.id) ?? null;
  const autoValues = useMemo(() => compiled?.autofill?.autoValues ?? {}, [compiled]);

  const legacyFields = useMemo(
    () =>
      new Map<string, EditorField>(
        ((getTemplateVersion(templateKey, templateVersion)?.variableSchema ?? []) as ReadonlyArray<EditorField>).map(
          (f) => [f.key, f],
        ),
      ),
    [templateKey, templateVersion],
  );
  const shown = useMemo(() => withLegacyOverrides(values), [values]);
  const form: CompositionForm | null = useMemo(() => {
    const composition = getComposition(templateKey, templateVersion, effectiveValues(null, shown, autoValues));
    if (!composition) return null;
    const described = describeCompositionForm(composition, registry());
    return withCustomFields(withLegacyAutofill(described, legacyFields), log);
  }, [templateKey, templateVersion, shown, autoValues, legacyFields, log]);
  const codes = useMemo(() => (form ? toOptionCodes(form.fields, shown) : shown), [form, shown]);
  const scope = useMemo(() => effectiveValues(form, codes, autoValues), [form, codes, autoValues]);
  const state = useMemo(() => (form ? computeFormState(form, scope) : null), [form, scope]);

  const onFieldChange = useCallback((key: string, next: unknown) => {
    setValues((v) => ({ ...v, [key]: next }));
    setDirty(true);
  }, []);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await tenantApi.contract_attachments.update_template_values({ id: attachment.id, values });
      setDirty(false);
      await onChanged();
    } catch (e) {
      setErr(
        e instanceof ApiError && e.message === 'custom_values_invalid'
          ? customValuesErrorText(e.data, (k) => form?.fields.find((f) => f.key === k)?.label ?? null)
          : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  };

  const switchMode = async (key: string, mode: 'auto' | 'manual') => {
    if (dirty) await save();
    setBusyKey(key);
    setErr(null);
    try {
      await tenantApi.contract_attachments.set_template_autofill_mode({ id: attachment.id, field_key: key, mode });
      await onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyKey(null);
    }
  };

  if (!form || !state) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
        Formuläret för den här bilagan finns inte i webbklienten ännu.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 gap-4">
      <div className="flex w-[26rem] shrink-0 flex-col gap-3 overflow-auto">
        <p className="text-[11px] text-stone-500">
          Bilagan är ett eget dokument. Uppgifter markerade som automatiska följer huvudavtalet tills du väljer
          Manuell.{' '}
          <Link to={`/contracts/${contractId}/beta/anpassa?dokument=${attachment.id}`} className="underline">
            Anpassa bilagans text
          </Link>
        </p>
        <BetaFormGroups
          form={form}
          state={state}
          values={codes}
          scope={scope}
          autoValues={autoValues}
          legacyFields={legacyFields}
          attachments={[]}
          disabled={disabled || busy}
          modeLocked={disabled}
          busyKey={busyKey}
          onFieldChange={onFieldChange}
          onSwitchMode={(key, mode) => void switchMode(key, mode)}
          onFocusInstance={() => {}}
          isGapShown={() => true}
          onFieldBlur={() => {}}
          mappedFromParent
        />
        {err && <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">{err}</div>}
        {!disabled && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={!dirty || busy}
              className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Sparar…' : 'Spara bilaga'}
            </button>
            {dirty && <span className="text-[11px] text-stone-500">Osparade ändringar</span>}
          </div>
        )}
      </div>
      <div className="flex min-h-[60vh] min-w-0 flex-1 flex-col border border-stone-200">
        {compiled ? (
          <IrPreviewFrame
            preview={{
              ...preview!,
              parts: [
                {
                  key: compiled.attachmentId,
                  title: compiled.title,
                  html: compiled.html,
                  letterheadHtml: compiled.letterheadHtml,
                  footerHtml: compiled.footerHtml,
                },
              ],
              attachments: [],
            }}
            focus={null}
          />
        ) : (
          <div className="p-4 text-xs text-stone-500">Laddar förhandsvisning…</div>
        )}
      </div>
    </div>
  );
}
